import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium cursor-pointer ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary variant (default) - Teal/Cyan (Medical/Healthcare theme)
        default:
          "bg-teal-600 text-white hover:bg-teal-700 active:scale-[0.98] dark:bg-teal-700 dark:hover:bg-teal-600",
        primary:
          "bg-teal-600 text-white hover:bg-teal-700 active:scale-[0.98] dark:bg-teal-700 dark:hover:bg-teal-600",

        // Secondary variant - Slate Gray
        secondary:
          "bg-slate-600 text-white hover:bg-slate-700 active:scale-[0.98] dark:bg-slate-700 dark:hover:bg-slate-600",

        // Danger/Destructive variant - Red
        destructive:
          "bg-red-600 text-white hover:bg-red-700 active:scale-[0.98] dark:bg-red-700 dark:hover:bg-red-600",
        danger:
          "bg-red-600 text-white hover:bg-red-700 active:scale-[0.98] dark:bg-red-700 dark:hover:bg-red-600",

        // Success variant - Green
        success:
          "bg-green-600 text-white hover:bg-green-700 active:scale-[0.98] dark:bg-green-700 dark:hover:bg-green-600",

        // Warning variant - Amber
        warning:
          "bg-amber-500 text-white hover:bg-amber-600 active:scale-[0.98] dark:bg-amber-600 dark:hover:bg-amber-500",

        // Info variant - Blue
        info: "bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] dark:bg-blue-700 dark:hover:bg-blue-600",

        // Outline variant
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",

        // Ghost variant
        ghost:
          "hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",

        // Link variant
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
